import { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as Actions from '../../../redux/actions/index';
import { Icon, ICONS } from 'components/Icons';
import Checkbox from 'components/elements/atoms/Checkbox';
import RectangularButton from 'components/elements/molecules/RectangularButton/RectangularButton';
import { SourcePicker } from 'routes/User/Welcome/WelcomeSurvey/steps/ReadingHabitsSourcesRead/SourcePickerChoice/SourcePickerChoice';
import { getLogo } from 'helpers/sourceIcon';
import ActionLink from 'components/elements/molecules/ActionLink/ActionLink';
import BreadCrumb from 'components/elements/molecules/BreadCrumb/BreadCrumb';
import './CreateCourseSource.scss';
import { FocusTrap } from 'focus-trap-react';

function CreateCourseSourceModal({
  onClose,
  courseSources,
  courseId,
  actions,
  courseSourcesNote,
}) {
  const [sources, setSources] = useState([...courseSources]);
  const [courseSource, setCourseSource] = useState(null);
  const [note, setNote] = useState(courseSourcesNote);
  const [saving, setSaving] = useState(false);

  const onCreateOrEdit = (source) => {
    if (source) {
      setCourseSource({ ...source });
    } else {
      setCourseSource({ courseId });
    }
  };

  const onCloseCreateCourseSource = (source) => {
    setCourseSource(null);

    if (source) {
      const index = sources.findIndex((i) => i.sourceId === source.sourceId);
      if (index === -1) {
        sources.push(source);
      } else {
        sources.splice(index, 1, source);
      }

      setSources([...sources]);
    }
  };

  const onRemove = async (source) => {
    const index = sources.indexOf(source);

    if (index !== -1) {
      sources.splice(index, 1);
      setSources([...sources]);
    }
  };

  const onSave = async () => {
    setSaving(true);

    try {
      actions.updateCourse({
        courseId,
        sourcesNote: note,
      });
      const initialSourceIds = courseSources.map((i) => i._id);
      const newSourceIds = sources.map((i) => i._id).filter((i) => !!i);
      const removedSourceIds = initialSourceIds.filter(
        (i) => !newSourceIds.includes(i),
      );
      const removedCourseSources = courseSources.filter((i) =>
        removedSourceIds.includes(i._id),
      );
      await Promise.all(
        removedCourseSources.map((source) =>
          actions.removeCourseSource(source.courseId, source.sourceId),
        ),
      );
      await Promise.all(
        sources.map((source) => actions.addOrUpdateCourseSource(source)),
      );
      await actions.getCourseSources(courseId);
      onClose();
    } catch {
    } finally {
      setSaving(false);
    }
  };

  const renderContent = () => {
    if (courseSource) {
      return (
        <>
          <BreadCrumb
            breadCrumb={[
              {
                label: 'Back to course source',
                icon: 'BACK',
                onClick: () => {
                  onCloseCreateCourseSource();
                },
              },
              { label: 'Add a recommended source' },
            ]}
          />
          <div className="header mt-2">Add a recommended source</div>
          <CreateCourseSource
            courseSource={courseSource}
            close={onCloseCreateCourseSource}
          />
        </>
      );
    }
    return (
      <>
        <div className="header">Course sources</div>
        <div className="font-weight-500 mb-4 font-size-12">
          <i>Use this optional section to recommend sources for this course.</i>
        </div>
        <CourseSourcesNote note={note} setNote={setNote} />
        <div className="d-flex align-items-center mt-4 mb-2">
          <h3 className="my-0 mr-3">Recommended sources</h3>
          <ActionLink
            linkClassName="av-blue p-2"
            onClick={() => onCreateOrEdit()}
            iconType="averPointIcon"
            iconName="ADD"
            iconClassName="mr-0"
            ariaLabel="Add"
          ></ActionLink>
        </div>
        {sources && (
          <>
            <ListSources
              sources={sources}
              onCreateOrEdit={onCreateOrEdit}
              onRemove={onRemove}
            />
          </>
        )}
        <div className="text-right mt-4">
          <RectangularButton onClick={onSave} disabled={saving}>
            Save
          </RectangularButton>
        </div>
      </>
    );
  };

  return (
    <Modal show className="create-course-source-modal">
      <FocusTrap>
        <div>
          <Modal.Body>
            {renderContent()}
            <Button
              bsStyle="link"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            >
              <Icon icon={ICONS.CLOSE} className="mr-0" />
            </Button>
          </Modal.Body>
        </div>
      </FocusTrap>
    </Modal>
  );
}

function mapStateToProps(state, { courseId }) {
  const course = state.classRoom.courses.find((i) => i.courseId === courseId);
  return {
    courseSources: state.courseSources[courseId],
    courseSourcesNote: course?.sourcesNote || '',
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Actions, dispatch),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CreateCourseSourceModal);

function useCreateCourseSource(courseSource, source) {
  const [model, setModel] = useState({
    subscriptionStatus: 'Free',
    ...courseSource,
  });
  const submitEnabled = model && model.sourceId && model.subscriptionStatus;

  return {
    model,
    setModel,
    submitEnabled,
  };
}

function CreateCourseSource({
  courseSource,
  // actions,
  close,
}) {
  const { model, setModel, submitEnabled } =
    useCreateCourseSource(courseSource);

  const onSubmit = async () => {
    close(model);
  };

  const renderNote = () => {
    if (!model.sourceId) {
      return null;
    }

    return (
      <textarea
        className="form-control my-4"
        placeholder={`Notes about ${model.sourceName}`}
        value={model.notes}
        onChange={(e) => setModel({ ...model, notes: e.target.value })}
      />
    );
  };

  return (
    <div className="create-course-source">
      <Sources
        source={{
          sourceName: model.sourceName,
          logo: model.logo,
          domains: model.domains,
        }}
        onChange={(source) =>
          setModel({
            ...model,
            sourceId: source?.sourceId,
            sourceName: source?.name,
            logo: source?.logo,
            // source,
            notes: source ? model.notes : '',
          })
        }
        // actions={actions}
      />
      <div className="d-flex align-items-center py-1">
        <Checkbox
          onClick={() => setModel({ ...model, subscriptionStatus: 'Free' })}
          checked={model.subscriptionStatus === 'Free'}
          labelClassName="pl-0"
          className="ml-0 mt-0"
        >
          Free
        </Checkbox>
        <Checkbox
          onClick={() =>
            setModel({ ...model, subscriptionStatus: 'SchoolSubscription' })
          }
          checked={model.subscriptionStatus === 'SchoolSubscription'}
          labelClassName="pl-0"
          className="ml-4 mt-0"
        >
          School Subscription
        </Checkbox>
        <Checkbox
          onClick={() =>
            setModel({ ...model, subscriptionStatus: 'NoSubscription' })
          }
          checked={model.subscriptionStatus === 'NoSubscription'}
          labelClassName="pl-0"
          className="ml-4 mt-0"
        >
          No Subscription
        </Checkbox>
      </div>
      {renderNote()}
      <div className="text-right">
        <RectangularButton onClick={onSubmit} disabled={!submitEnabled}>
          Add
        </RectangularButton>
      </div>
    </div>
  );
}

function CourseSourcesNote({ note, setNote }) {
  return (
    <div className="course-sources-note">
      <label
        className="my-0 font-weight-normal"
        htmlFor="create-course-source-enter-note"
      >
        Teacher's note about sources
      </label>
      <textarea
        className="form-control mt-2"
        placeholder="Enter note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        id="create-course-source-enter-note"
      />
    </div>
  );
}

function Sources({ onChange, source }) {
  const renderSourcePicker = () => {
    if (source && source.sourceName) {
      return (
        <div className="d-flex align-items-center my-4">
          <img
            src={getLogo({
              logo: source.logo,
              domain: source.domains ? source.domains[0] : null,
            })}
            width={25}
            className="mr-3"
            alt={source.sourceName}
          />
          <span className="font-weight-600">{source.sourceName}</span>
          <ActionLink
            iconType="averPointIcon"
            iconName="CLOSE"
            iconClassName="av-red"
            linkClassName="ml-4 p-2"
            onClick={() => {
              onChange(null);
            }}
            ariaLabel="Close"
          />
        </div>
      );
    } else {
      return (
        <SourcePicker
          onChange={(_value, fullSource) => {
            if (fullSource.sourceId) {
              onChange(fullSource);
            }
          }}
          value={[]}
          label="Search for a source"
          allowSearch={false}
        />
      );
    }
  };

  return renderSourcePicker();
}

function ListSources({ sources, onCreateOrEdit, onRemove }) {
  return (
    <>
      {sources.map((source) => (
        <Source
          source={source}
          key={source._id || source.sourceName}
          onEdit={onCreateOrEdit}
          onRemove={onRemove}
        />
      ))}
      {!sources.length && (
        <div className="font-size-12 gray-1">
          <i>No recommended sources</i>
        </div>
      )}
    </>
  );
}

function Source({ source, onEdit, onRemove }) {
  function renderIcon() {
    if (!source.logo && !source.domains?.length) return null;

    return (
      <img
        src={getLogo({
          logo: source.logo,
          domain: source.domains ? source.domains[0] : null,
        })}
        width={20}
        className="mr-3"
        alt={source.sourceName}
      />
    );
  }

  const renderSubscriptionStatus = () => {
    switch (source.subscriptionStatus) {
      case 'Free':
        return 'Free';
      case 'SchoolSubscription':
        return 'Free with school subscription';
      case 'NoSubscription':
        return 'Paid';
      default:
        return source.subscriptionStatus;
    }
  };

  const renderNotes = () =>
    source.notes ? (
      <div
        dangerouslySetInnerHTML={{ __html: source.notes }}
        className="source-notes"
      />
    ) : null;

  return (
    <div className="my-3 d-flex">
      <div className="flex-shrink-0">{renderIcon()}</div>
      <div className="flex-fill">
        <div>
          <strong>{source.sourceName}</strong> ({renderSubscriptionStatus()})
        </div>
        {renderNotes()}
      </div>
      <div className="flex-shrink-0">
        <ActionLink
          iconType="averPointIcon"
          iconName="PENCIL"
          iconClassName="av-blue"
          onClick={() => onEdit(source)}
          ariaLabel="Edit"
        />
        <ActionLink
          iconType="averPointIcon"
          iconName="DELETE"
          linkClassName="ml-3"
          iconClassName="av-red mr-0"
          onClick={() => onRemove(source)}
          ariaLabel="Remove"
        />
      </div>
    </div>
  );
}
